// @givmo/app-charity — the personal-email HARD BLOCK's server read (CPA-WEB53, D-CP-63).
//
// Jonathan's ruling: a personal-email address never proceeds through the normal process. The list of
// what counts as "personal" is the backend's `DOMAIN_BLACKLIST` (`app/constants.py`) — the SAME list
// the employer portal's sign-up refuses — and this app never carries a copy of it: every verdict comes
// from the backend's availability read, `GET /admin/users/email_available?email=…`
// (`user_service.check_admin_email`), which the employer portal's `@givmo/auth` flows already call.
// It has no auth dependency (the identity middleware answers `None` without a bearer, the route has no
// permission decorator, and every charity origin is on the CORS allowlist), so the sign-up form can ask
// BEFORE a Firebase account exists — which is the whole point: the refused address never becomes an
// account, never reaches `/verify-email`, and never spends an OTP.
//
// ★ THE BLACKLIST ANSWER IS DISTINGUISHED, NOT INFERRED. That read refuses THREE ways — a personal
// domain, an address already registered, a domain already registered as a COMPANY — all with
// `available: false`. Only the first is this door's business: a nonprofit whose domain is also an
// employer account on Givmo must still be able to sign up here, and an address that already exists is
// Firebase's `emailAlreadyInUse` to report with its "Sign in instead" recovery. So the verdict keys on
// the backend's typed `reason` code — `personal_email_domain`, minted on the blacklist branch by
// CPA-BE30's backend PR on the already-declared optional `CheckEmailOut.reason` field — AND ON
// NOTHING ELSE.
//
// ★ THE MESSAGE LITERAL IS GONE, AND MUST NOT COME BACK (CPA-WEB54). Until the reason code reached
// production this decode also blocked on the branch's exact sentence, "Email is not available for
// usage", so a deployed backend that predated the code still refused. That coupling was always meant
// to die the day the code shipped, and it has: `2423daa7e` is an ancestor of the backend's
// `origin/main`, `origin/staging` AND `origin/development`, and prod has served it since
// 2026-09-12T15:10Z (`0f2888866`, `main.yml` run 34699800042). A backend that reworded that sentence
// must keep refusing, and a backend that borrowed it for a different refusal must not — which is
// exactly what a typed code buys and a string cannot. The backend still SENDS the message; this door
// simply does not read it.
//
// ★ FAIL OPEN, DELIBERATELY. A transport failure, a 4xx, an unreadable body — anything that is not a
// clear "this domain is personal" — answers `allowed`, so an outage of one availability read can never
// lock every nonprofit out of signing up or claiming. The block is belt-and-braces (both doors ask),
// and the claim engine's Tier-1 conjunct (c) still refuses instant verification to a personal domain
// on the backend regardless of what either door decided.
//
// ★ NORMALISE BEFORE ASKING. Firebase lower-cases an address at account creation and the backend
// compares the typed domain against a lower-case list, so `Ada@Gmail.com` must reach the wire as
// `ada@gmail.com` or the two doors would disagree with the account they gate.

import { queryOptions } from '@tanstack/react-query';
import { sdk } from '@givmo/api-client';

/** The backend's typed answer for a blacklisted domain (`user_service.check_admin_email`). */
export const PERSONAL_EMAIL_DOMAIN_REASON = 'personal_email_domain';

/** What a door does with an address. */
export type PersonalEmailVerdict = 'blocked' | 'allowed';

/** The address as the wire and the Firebase account both spell it. */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** Narrow an unknown value to a plain object. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Read the availability body into a verdict. `blocked` ONLY on the recognised personal-domain refusal:
 * `available: false` together with the typed `reason` code. Everything else — `available: true`, the
 * other two refusals, a refusal carrying no reason, a body of any other shape — is `allowed`: the door
 * fails open, never towards locking a nonprofit out.
 */
export function decodePersonalEmailVerdict(body: unknown): PersonalEmailVerdict {
  if (!isRecord(body)) return 'allowed';
  if (body.available !== false) return 'allowed';
  return body.reason === PERSONAL_EMAIL_DOMAIN_REASON ? 'blocked' : 'allowed';
}

/**
 * Ask the backend whether `email`'s domain is personal. Never throws and never reaches the wire with
 * an un-normalised address; any failure answers `allowed` (see the header).
 */
export async function checkPersonalEmailDomain(email: string): Promise<PersonalEmailVerdict> {
  try {
    const { data, error } = await sdk.adminCheckEmailAvailable({
      query: { email: normalizeEmail(email) },
    });
    if (error !== undefined && error !== null) return 'allowed';
    return decodePersonalEmailVerdict(data);
  } catch {
    return 'allowed';
  }
}

/**
 * The verdict as a query, for a door that renders under the app's `QueryClientProvider` (the claim
 * wizard's entry). Keyed by the normalised address and never stale: a domain does not change its
 * nature within a session, and the app's identity boundary clears the whole cache on any uid change.
 */
export function personalEmailBlockQueryOptions(email: string) {
  return queryOptions({
    queryKey: ['personal-email-block', normalizeEmail(email)] as const,
    queryFn: () => checkPersonalEmailDomain(email),
    staleTime: Infinity,
  });
}
